import { take } from 'rxjs/operators';
import { Component, OnInit } from '@angular/core';
import { SitesService, Site } from '@app/data/services/sitesService';
import { FilterService } from '@app/data/services/filterService';
import { SelectedLocationService, SiteLocation } from '@app/data/services/locationServices';
import { ChartService } from '@app/data/services/chartService';
import { EChartsOption } from 'echarts';
import { getRoseChartConfig, RoseChartData } from './rongo-mauri-chart-helpers';
import { formatDateForApi } from '@app/data/helpers/dataHelpers';
import { RongoReportingData, RongoSourceGroup } from '@app/widgets/helpers/types';
import { withOrganisationBreakdown } from '@app/widgets/helpers/organisation-breakdown';

/**
 * Only intended use is for Rongo Mauri
 * If we require a rose chart elsewhere, data and config fetching should be more dynamic
 */
@Component({
  selector: 'app-rongo-mauri-chart',
  templateUrl: './rongo-mauri-chart.component.html',
  styleUrls: ['./rongo-mauri-chart.component.scss'],
})
export class RongoMauriChartComponent implements OnInit {
  chartTitle: string = 'Rongo Mauri';
  chartSubtitle: string = '';
  chartOptions?: EChartsOption;
  id: string = 'rongo-mauri-rose';
  startDate: any = '';
  endDate: any = '';
  location: SiteLocation | null = null;
  site: Site | null = null;
  noData: boolean = true; // We can do this check in html ngIf, but it's hard to read for some widgets
  breakdown: RongoSourceGroup<RoseChartData[]>[] | null = null;

  constructor(
    private filterService: FilterService,
    private sitesService: SitesService,
    private chartService: ChartService,
    private selectedLocationService: SelectedLocationService
  ) {}

  ngOnInit() {
    this.selectedLocationService.subscribe(this.handleLocationUpdate);
    this.startDate = formatDateForApi(this.filterService.getFilters().fromDate);
    this.endDate = formatDateForApi(this.filterService.getFilters().toDate);
    this.filterService.subscribe(this.handleFilterUpdate);
    this.sitesService
      .getSelectedSite()
      .pipe(take(1))
      .subscribe((site) => {
        this.handleSiteUpdate({ site });
        this.sitesService.subscribe(this.handleSiteUpdate);
      });
  }
  setData = () => {
    if (!this.startDate || !this.endDate || !this.site) return;
    /**
     * test data: awaiting endpoint
     */
    this.chartService
      .getMauriRoseData(this.startDate, this.endDate, this.location ? this.location.id : null, 'organisation')
      .subscribe(
        (data: RongoReportingData<RoseChartData[]>) => {
          const { config, data: mauriRoseData, breakdown } = data;
          this.chartTitle = config?.title || '';
          this.chartSubtitle = config?.subTitle || '';
          this.breakdown = breakdown?.groups || null;
          this.noData = mauriRoseData === undefined || mauriRoseData.length === 0;
          this.chartOptions = withOrganisationBreakdown(getRoseChartConfig(mauriRoseData), this.breakdown, {
            buildGroupOption: (d) => getRoseChartConfig(d),
            flatten: (d: RoseChartData[]) => d.map((item) => ({ name: item.textValue, value: item.count })),
          });
        },
        (error: any) => {
          console.error('Error fetching data:', error);
        }
      );
  };
  handleSiteUpdate = ({ site }: any) => {
    if (site && site.id != this.site?.id) {
      this.site = site;
      this.setData();
    }
  };
  handleLocationUpdate = (location: SiteLocation | null) => {
    if (!location && !this.location) return;
    if (location?.id != this.location?.id) {
      this.location = location;
      this.setData();
    }
  };
  handleFilterUpdate = (filters: any) => {
    if (!filters.filters.fromDate || !filters.filters.toDate) return; // Bail out until we have full date range

    const newStartDate = formatDateForApi(filters.filters.fromDate);
    const newEndDate = formatDateForApi(filters.filters.toDate);
    const sharedFiltersChanged = this.sharedRongoReportFiltersChanged(filters.changedFilters);
    if (
      (newStartDate !== this.startDate || newEndDate !== this.endDate || sharedFiltersChanged) &&
      !!newStartDate &&
      !!newEndDate
    ) {
      this.startDate = newStartDate;
      this.endDate = newEndDate;
      this.setData();
    }
  };

  private sharedRongoReportFiltersChanged(changedFilters: string[] | undefined): boolean {
    return !!changedFilters?.some((filter) =>
      ['selectedContributingOrganisations', 'selectedContributingProjectListItems'].includes(filter)
    );
  }
}
